import { AspectRatio, Center, Group, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconVerified } from 'ui-icons';

import { Badge, Card, CardProps, ImageMantine } from '../../../../../DataDisplay';
import classes from '../CauseCard.module.css';

interface CauseStoreCardProps extends CardProps {
  direction?: 'row' | 'column';
  imgSrc?: string;
  title?: string;
  info?: { detail?: React.ReactNode; tag?: string };
  supporting?: boolean;
  onClick?: () => void;
  titleLineClamp?: number;
  isVerified?: boolean;
}

const RowCard = ({
  imgSrc,
  title,
  info,
  supporting,
  onClick,
  titleLineClamp = 1,
  ...props
}: CauseStoreCardProps) => {
  return (
    <Card
      className={classes.row_card}
      style={supporting ? { boxShadow: 'none' } : {}}
      component={onClick ? 'button' : 'div'}
      onClick={onClick}
      {...props}
    >
      <Stack gap={0} w="100%">
        {supporting && (
          <Center className={classes.supporting}>
            <Text fz="xl" c="white" fw={600}>
              You Are Supporting This Cause
            </Text>
          </Center>
        )}
        <AspectRatio ratio={3 / 2}>
          <ImageMantine src={imgSrc} className={classes.banner_img} />
        </AspectRatio>

        <Stack p="sm" gap={4} ta="left">
          <Text fz="sm" fw={600} mih={21} lineClamp={titleLineClamp}>
            {title}
          </Text>
          {info && (
            <Stack gap="xs">
              <Text fz={11} c="gray">
                {info?.detail}
              </Text>
              {info?.tag && (
                <Badge type="info" variant="filled">
                  {info?.tag}
                </Badge>
              )}
            </Stack>
          )}
        </Stack>
      </Stack>
    </Card>
  );
};

const ColumnCard = ({
  imgSrc,
  title,
  info,
  onClick,
  isVerified = false,
  ...props
}: CauseStoreCardProps) => {
  return (
    <Card
      className={classes.column_card}
      component={onClick ? 'button' : 'div'}
      onClick={onClick}
      {...props}
    >
      <Group gap="sm" wrap="nowrap" align="flex-start">
        <ImageMantine src={imgSrc} radius={12} h={48} w={48} className={classes.thumbnail} />
        <Stack gap={4} w="100%" ta="left">
          <Text fz="sm" fw={600} lineClamp={1}>
            {title}
          </Text>
          <Group gap="xs">
            {isVerified && (
              <ThemeIcon color="cobalt.5" variant="transparent" size={16}>
                <IconVerified />
              </ThemeIcon>
            )}
            {info && (
              <Group gap="sm" justify="space-between">
                {info?.detail && (
                  <Text fz={11} c="gray">
                    {info?.detail}
                  </Text>
                )}
                {info?.tag && (
                  <Badge type="info" variant="filled">
                    {info?.tag}
                  </Badge>
                )}
              </Group>
            )}
          </Group>
        </Stack>
      </Group>
    </Card>
  );
};

const CauseStoreCard = ({
  direction = 'row',
  imgSrc,
  title,
  info,
  supporting,
  titleLineClamp = 1,
  ...props
}: CauseStoreCardProps) => {
  return direction === 'row' ? (
    <RowCard
      imgSrc={imgSrc}
      title={title}
      info={info}
      supporting={supporting}
      titleLineClamp={titleLineClamp}
      {...props}
    />
  ) : (
    <ColumnCard imgSrc={imgSrc} title={title} info={info} supporting={supporting} {...props} />
  );
};

export { CauseStoreCard };
